import React from "react";
import SEO from "../components/SEO"; 
import { useTheme } from "../context/ThemeContext";

/* ------------------ MAIN WRAPPER (LegalPage) ------------------ */
export const LegalPage = ({ title, subtitle, children }) => {
  const { isDarkMode } = useTheme();

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-white text-black'}`}>
      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-32 pb-32 space-y-20">

        {/* HEADER CHASSIS - Pure High-End Theme */}
        <div className={`relative overflow-hidden rounded-[2.5rem] border-2 p-10 md:p-16 transition-all ${isDarkMode ? 'bg-transparent border-white/10' : 'bg-transparent border-black/10'}`}>
          
          {/* Signature Emerald Glow */}
          <div className={`absolute -top-24 -right-24 w-64 h-64 blur-[120px] rounded-full ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-500/5'}`} />

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
               <div className="w-8 h-[2px] bg-emerald-500"></div>
               <p className="text-[10px] font-black uppercase tracking-[0.5em] text-emerald-500">System_Protocol_v2.0</p>
            </div>
            <h1 className="text-5xl md:text-8xl font-black italic uppercase tracking-tighter leading-none mb-8">
              {title}
            </h1>
            <p className={`max-w-2xl text-sm italic font-medium leading-relaxed border-l-2 border-emerald-500/30 pl-6 ${isDarkMode ? 'text-white/50' : 'text-black/50'}`}>
              {subtitle}
            </p>
          </div>
        </div>

        {/* CONTENT CHASSIS */}
        <div className="space-y-16">{children}</div>
      </div>
    </div>
  );
};

/* ------------------ SECTION CARD ------------------ */
export const Section = ({ num, title, text }) => {
  const { isDarkMode } = useTheme();

  return (
    <div className="group grid md:grid-cols-12 gap-8 items-start">

      {/* INDEX STRIP */}
      <div className="md:col-span-2 flex items-center gap-6">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-xl border-2 transition-all duration-500 group-hover:border-emerald-500 ${isDarkMode ? 'bg-transparent border-white/10 text-emerald-500' : 'bg-transparent border-black/10 text-black'}`}>
          {num}
        </div>
        <div className={`hidden md:block h-[1px] flex-1 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`} />
      </div>

      {/* CONTENT BLOCK - No Zinc, Just Pure Borders */}
      <div className={`md:col-span-10 relative border-2 rounded-[2.5rem] p-10 transition-all duration-500 ${isDarkMode ? 'bg-transparent border-white/5 hover:border-emerald-500/30' : 'bg-transparent border-black/5 hover:border-emerald-500/30'}`}>
        
        <h3 className={`text-2xl font-black italic uppercase tracking-tighter mb-4 transition-colors ${isDarkMode ? 'text-white' : 'text-black'} group-hover:text-emerald-500`}>
          {title}
        </h3>

        <p className={`text-[12px] leading-[1.8] font-bold uppercase tracking-wide opacity-60`}>
          {text}
        </p>
      </div>
    </div>
  );
};

/* ------------------ TERMS PAGE ------------------ */
const Terms = () => {
  return (
    <>
      <SEO 
        title="Terms of Service | Legal Governance" 
        description="The legal framework of THE_ARCHIVE. Guidelines for global acquisitions and digital governance."
        url="/terms"
      />

      <LegalPage title="Terms" subtitle="Digital Governance & Archive Usage Agreement for Global Acquisitions.">

        <Section 
          num="01" 
          title="Authenticity Protocol" 
          text="Every asset listed within the THE_ARCHIVE vault undergoes a rigorous multi-stage verification process. We provide a 100% reclamation guarantee if a piece is verified as non-authentic by an authorized third-party agency. Unauthorized commercial sabotage will result in a permanent terminal ban." 
        />

        <Section 
          num="02" 
          title="Condition Standards" 
          text="Heritage assets carry the characteristic DNA of time. We provide high-definition visuals and status reports for every piece. Grading from 'Archive New' to 'Well-Loved' is final. Microscopic signs of aging are inherent to heritage items and do not constitute grounds for dispute." 
        />

        <Section 
          num="03" 
          title="Digital Sovereignty" 
          text="All visual assets, descriptions, and intellectual property are the exclusive property of THE_ARCHIVE. Unauthorized commercial reselling or use of our media is strictly prohibited. Automated data scraping will trigger immediate IP-nullification and order termination." 
        />

        <Section 
          num="04" 
          title="Legal Jurisdiction" 
          text="Liability is strictly capped at the original transaction value. THE_ARCHIVE is not responsible for manufacturer-related decay on legacy items (10+ years old). All legal disputes are subject to the exclusive jurisdiction of Mumbai, India." 
        />

      </LegalPage>
    </>
  );
};

export default Terms;